/**
 * Painted-bounds compression for ViewerTopBar.
 * Wrap when task section content escapes its flex item (into the inter-item gap)
 * or crosses rightCluster.left; label collapse uses controls vs rightCluster.
 */

/**
 * Rightmost painted edge of an element subtree (includes overflowing children).
 * @param {Element | null | undefined} el
 * @returns {number}
 */
export function paintedRight(el) {
    if (!el) return 0;
    let right = el.getBoundingClientRect().right;
    for (const child of el.children) {
        right = Math.max(right, paintedRight(child));
    }
    return right;
}

/**
 * Rightmost painted edge of descendant content only (excludes the root border box).
 * Use for flex items that can span full width while content stays narrow (row-2 task section).
 * @param {Element | null | undefined} el
 * @returns {number}
 */
export function paintedContentRight(el) {
    if (!el) return 0;
    if (!el.children?.length) {
        return el.getBoundingClientRect().right;
    }
    let right = 0;
    for (const child of el.children) {
        right = Math.max(right, paintedContentRight(child));
    }
    return right;
}

/**
 * @param {{
 *   section: Element,
 *   controls: Element | null | undefined,
 *   rightCluster: Element,
 *   topBar: Element,
 * }} els
 * @returns {{
 *   paintOverlapPx: number,
 *   sectionOverflowPx: number,
 *   runwayPx: number,
 *   neededPaintPx: number,
 *   controlsPaintRight: number,
 *   sectionPaintRight: number,
 *   rightClusterLeft: number,
 *   topBarWidth: number,
 * }}
 */
export function measureTopBarPaint({ section, controls, rightCluster, topBar }) {
    const sectionRect = section.getBoundingClientRect();
    const rightRect = rightCluster.getBoundingClientRect();
    const sectionPaintRight = paintedContentRight(section);
    const controlsPaintRight = paintedContentRight(controls);
    const rightClusterLeft = rightRect.left;

    return {
        paintOverlapPx: Math.max(0, sectionPaintRight - rightClusterLeft),
        sectionOverflowPx: Math.max(0, sectionPaintRight - sectionRect.right),
        runwayPx: rightClusterLeft - sectionRect.left,
        neededPaintPx: Math.max(0, sectionPaintRight - sectionRect.left),
        controlsPaintRight,
        sectionPaintRight,
        rightClusterLeft,
        topBarWidth: topBar.clientWidth,
    };
}

/**
 * @param {{ labelCollapsed: boolean, taskRowWrapped: boolean }} prev
 * @param {{
 *   paintOverlapPx: number,
 *   sectionOverflowPx: number,
 *   controlsPaintRight: number,
 *   rightClusterLeft: number,
 *   labelWidth: number,
 *   topBarWidth: number,
 *   wrapTriggerWidth: number | null,
 *   taskSectionLabelMode?: 'normal' | 'sr-only',
 * }} metrics
 * @param {{ collapseSlop?: number, expandSlop?: number, wrapSlop?: number, unwrapSlop?: number }} [opts]
 * @returns {{ labelCollapsed: boolean, taskRowWrapped: boolean, wrapTriggerWidth: number | null }}
 */
export function nextTopBarCompression(prev, metrics, opts) {
    const collapseSlop = opts?.collapseSlop ?? 8;
    const expandSlop = opts?.expandSlop ?? 16;
    const wrapSlop = opts?.wrapSlop ?? 1;
    const unwrapSlop = opts?.unwrapSlop ?? 40;

    const {
        paintOverlapPx,
        sectionOverflowPx,
        controlsPaintRight,
        rightClusterLeft,
        labelWidth,
        topBarWidth,
        wrapTriggerWidth,
    } = metrics;
    const taskSectionLabelMode = metrics.taskSectionLabelMode ?? 'normal';
    const srOnly = taskSectionLabelMode === 'sr-only';

    let { labelCollapsed, taskRowWrapped } = prev;
    let nextWrapTriggerWidth = wrapTriggerWidth;

    if (srOnly) {
        labelCollapsed = true;
    }

    // Label visible: collapse when section overflows (protected UI no longer fits) or paint crowds right cluster.
    const shouldCollapseLabel = sectionOverflowPx > collapseSlop
        || paintOverlapPx > collapseSlop
        || controlsPaintRight + collapseSlop > rightClusterLeft;
    // Label hidden: project label back into layout before deciding to expand.
    const labelFitsExpanded = controlsPaintRight + labelWidth + expandSlop <= rightClusterLeft;

    const sectionContained = sectionOverflowPx <= 0;

    if (taskRowWrapped) {
        const canUnwrap = wrapTriggerWidth != null
            && topBarWidth > wrapTriggerWidth + unwrapSlop;

        if (canUnwrap) {
            if (!srOnly) {
                labelCollapsed = !labelFitsExpanded;
            }
            return {
                labelCollapsed: srOnly || labelCollapsed,
                taskRowWrapped: false,
                wrapTriggerWidth: null,
            };
        }
        return {
            labelCollapsed: srOnly || labelCollapsed,
            taskRowWrapped: true,
            wrapTriggerWidth: nextWrapTriggerWidth,
        };
    }

    const shouldWrap = (labelCollapsed || srOnly)
        && (sectionOverflowPx > wrapSlop || paintOverlapPx > wrapSlop);
    if (shouldWrap) {
        return {
            labelCollapsed: srOnly,
            taskRowWrapped: true,
            wrapTriggerWidth: topBarWidth,
        };
    }

    if (!srOnly) {
        if (!labelCollapsed) {
            if (shouldCollapseLabel) {
                labelCollapsed = true;
            }
        } else if (sectionContained && paintOverlapPx <= 0 && labelFitsExpanded) {
            labelCollapsed = false;
        }
    }

    return {
        labelCollapsed,
        taskRowWrapped: false,
        wrapTriggerWidth: nextWrapTriggerWidth,
    };
}
